/**
 * Environment Configuration for SAUBHAGYA Web Admin
 * Manages API endpoints, features, and microservices connectivity.
 *
 * Service URLs come from serviceConfig.ts (single source of truth).
 * To change a service URL/port, update ONLY the .env file.
 */

import { SERVICE_URLS as serviceUrls, getServiceUrl } from './serviceConfig';

// Environment Types
export type Environment = 'dev' | 'qa' | 'production';

export interface EnvironmentConfig {
  name: Environment;
  apiBaseUrl: string;
  microservices: {
    auth: string;
    iot: string;
    biogas: string;
    commerce: string;
    government: string;
    gaushala: string;
    purification: string;
    manufacturing: string;
    transport: string;
    notification: string;
  };
  features: {
    enableMockData: boolean;
    enableAnalytics: boolean;
    enableRealTimeUpdates: boolean;
  };
  oauth2: {
    clientId: string;
    clientSecret: string;
    scope: string;
  };
}

// Get current environment
function getCurrentEnvironment(): Environment {
  const env = import.meta.env.VITE_ENVIRONMENT as Environment;
  return env || 'dev';
}

// Shared microservices config - ALL environments use the same serviceConfig
const microservices = {
  auth: serviceUrls.auth,
  iot: serviceUrls.iot,
  biogas: serviceUrls.biogas,
  commerce: serviceUrls.commerce,
  government: serviceUrls.government,
  gaushala: serviceUrls.gaushala,
  purification: serviceUrls.purification,
  manufacturing: serviceUrls.manufacturing,
  transport: serviceUrls.transport,
  notification: serviceUrls.notification,
};

// Environment Configurations
const ENVIRONMENTS: Record<Environment, EnvironmentConfig> = {
  dev: {
    name: 'dev',
    apiBaseUrl: serviceUrls.auth,
    microservices,
    features: {
      enableMockData: false,
      enableAnalytics: true,
      enableRealTimeUpdates: true,
    },
    oauth2: {
      clientId: 'saubhagya-web',
      clientSecret: 'web-app-secret-2024',
      scope: 'iot-service commerce-service government-service profile',
    },
  },
  qa: {
    name: 'qa',
    apiBaseUrl: serviceUrls.auth,
    microservices,
    features: {
      enableMockData: false,
      enableAnalytics: true,
      enableRealTimeUpdates: true,
    },
    oauth2: {
      clientId: 'saubhagya-web-qa',
      clientSecret: import.meta.env.VITE_OAUTH2_CLIENT_SECRET || 'qa-secret',
      scope: 'iot-service commerce-service government-service profile',
    },
  },
  production: {
    name: 'production',
    apiBaseUrl: serviceUrls.auth,
    microservices,
    features: {
      enableMockData: false,
      enableAnalytics: true,
      enableRealTimeUpdates: true,
    },
    oauth2: {
      clientId: 'saubhagya-web-prod',
      clientSecret: import.meta.env.VITE_OAUTH2_CLIENT_SECRET || '',
      scope: 'iot-service commerce-service government-service profile',
    },
  },
};

// Get environment configuration
export function getEnvironmentConfig(): EnvironmentConfig {
  const currentEnv = getCurrentEnvironment();
  return ENVIRONMENTS[currentEnv];
}

// Environment variables with defaults
export const ENV = {
  ENVIRONMENT: getCurrentEnvironment(),
  API_BASE_URL: import.meta.env.VITE_API_BASE_URL || serviceUrls.auth,
  OAUTH2_CLIENT_ID: import.meta.env.VITE_OAUTH2_CLIENT_ID || ENVIRONMENTS.dev.oauth2.clientId,
  OAUTH2_CLIENT_SECRET: import.meta.env.VITE_OAUTH2_CLIENT_SECRET || ENVIRONMENTS.dev.oauth2.clientSecret,
  ENABLE_MOCK_DATA: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS !== 'false',
  ENABLE_REAL_TIME_UPDATES: import.meta.env.VITE_ENABLE_REAL_TIME_UPDATES !== 'false',
};

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/api/v1/login',
    LOGOUT: '/auth/api/v1/logout',
    REGISTER: '/auth/api/v1/register',
    ME: '/auth/api/v1/me',
    REFRESH: '/auth/api/v1/refresh',
    OAUTH2_TOKEN: '/auth/oauth2/token',
    JWKS: '/auth/.well-known/jwks.json',
    CATTLE_LIST: '/auth/api/v1/cattle/list',
    CATTLE_STORE: '/auth/api/v1/cattle/store',
  },
  IOT: {
    DEVICES: '/iot/api/v1/devices',
    RFID_TAGS: '/iot/api/v1/rfid-tags',
    SENSORS: '/iot/api/v1/sensors',
    DEVICE_STATUS: (deviceId: string) => `/iot/api/v1/devices/${deviceId}/status`,
  },
  TRANSACTION: {
    TRANSACTIONS: '/api/v1/transactions',
    CONTRIBUTIONS: '/api/v1/contributions',
    BIOGAS_PRODUCTION: '/api/v1/biogas-production',
    TRANSACTION_BY_ID: (id: string) => `/api/v1/transactions/${id}`,
  },
  SALES: {
    SALES: '/api/commerce/sales',
    ORDERS: '/api/commerce/orders',
    CONTRIBUTIONS: '/api/commerce/contributions',
    ORDER_BY_ID: (id: string) => `/api/commerce/orders/${id}`,
  },
  REPORTING: {
    DASHBOARD: '/api/v1/dashboard',
    ANALYTICS: '/api/v1/analytics',
    REPORTS: '/api/v1/reports',
    ANALYTICS_BY_TYPE: (type: string) => `/api/v1/analytics/${type}`,
  },
  GOVERNMENT: {
    DASHBOARD: '/government/api/v1/dashboard',
    SCHEMES: '/government/api/v1/schemes',
    COMPLIANCE: '/government/api/v1/compliance',
    SCHEME_BY_ID: (id: string) => `/government/api/v1/schemes/${id}`,
  },
  HEALTH: {
    AUTH: '/auth/actuator/health',
    IOT: '/iot/actuator/health',
    TRANSACTION: '/actuator/health',
    SALES: '/actuator/health',
    REPORTING: '/actuator/health',
    GOVERNMENT: '/government/actuator/health',
  },
};

// Service URLs for direct access (backward compatible)
export const SERVICE_URLS = {
  AUTH: serviceUrls.auth,
  IOT: serviceUrls.iot,
  BIOGAS: serviceUrls.biogas,
  COMMERCE: serviceUrls.commerce,
  GOVERNMENT: serviceUrls.government,
  GAUSHALA: serviceUrls.gaushala,
  PURIFICATION: serviceUrls.purification,
  MANUFACTURING: serviceUrls.manufacturing,
  TRANSPORT: serviceUrls.transport,
  NOTIFICATION: serviceUrls.notification,
};

// Export current config
export const CONFIG = getEnvironmentConfig();

export default CONFIG;
